import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import '../styles/auth.css';

// hCaptcha placeholder - in production use @hcaptcha/react-hcaptcha
function MockCaptcha({ onVerify }) {
  const [verified, setVerified] = useState(false);

  const handleVerify = () => {
    // In development, use bypass token
    setVerified(true);
    onVerify('dev-bypass');
  };

  return (
    <div className="captcha-box">
      {verified ? (
        <div className="captcha-verified">✅ Verified</div>
      ) : (
        <button type="button" className="captcha-btn" onClick={handleVerify}>
          <span className="captcha-check">☐</span>
          I'm not a robot
        </button>
      )}
      <div className="captcha-logo">🔒 hCaptcha</div>
    </div>
  );
}

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ email: '', password: '' });
  const [captchaToken, setCaptchaToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captchaToken) {
      setError('Please complete the captcha');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await login(form.email, form.password, captchaToken);
      navigate('/songs');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page page-enter">
      <div className="auth-glow" />

      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-icon animate-float">🎤</div>
          <h1 className="auth-title">Welcome Back</h1>
          <p className="auth-subtitle">Sign in to start singing</p>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              name="email"
              className="form-input"
              placeholder="you@example.com"
              value={form.email}
              onChange={handleChange}
              required
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                className="form-input"
                placeholder="Your password"
                value={form.password}
                onChange={handleChange}
                required
                autoComplete="current-password"
                style={{ paddingRight: 48 }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', fontSize: 18,
                }}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          <div style={{ textAlign: 'right', marginTop: -8, marginBottom: 16 }}>
            <Link to="/forgot-password" className="auth-link" style={{ fontSize: 13 }}>
              Forgot password?
            </Link>
          </div>

          <MockCaptcha onVerify={setCaptchaToken} />

          <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%', marginTop: 20 }}
            disabled={loading}>
            {loading ? (
              <><span className="animate-spin" style={{ display: 'inline-block' }}>⏳</span> Signing in...</>
            ) : (
              '🎤 Sign In'
            )}
          </button>
        </form>

        <div className="auth-footer">
          <p className="text-secondary text-sm">
            Don't have an account?{' '}
            <Link to="/register" className="auth-link">Create one free</Link>
          </p>
        </div>
      </div>

      {/* Decorative notes */}
      <div className="auth-notes">
        {['♪', '♫', '♩', '♬'].map((note, i) => (
          <span key={i} className={`auth-note delay-${(i + 1) * 100}`}>{note}</span>
        ))}
      </div>
    </div>
  );
}
